import * as React from 'react';

import { useBreakpointValue } from '@notino/react-styleguide';
import {
  GetCatalogProductViewQuery,
  ProductIdType,
  StockAvailability,
} from '@notino/shared/definitions/types';

import { ProductSlider } from '@components/ProductSlider';
import { useGetProductItemProps } from '@components/Universals/ProductItem/useGetProductItemProps';
import {
  useTrackToCartAsync,
  useTrackToCartFailedAsync,
} from '@containers/WishList/components/WishlistPager/tracking';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import { useProductsByIdsBatched } from '../../ProductsSection/useProductsByIdsBatched';

import { ShowOtherVariantsLink } from './components/ShowOtherVariants';
import { Unavailable } from './components/Unavailable';
import { SliderWrapper, StyledLoadingPlaceholder } from './styled';
import {
  GTM_LIST_LABEL,
  trackAccessories,
  trackElementClick,
} from './tracking';

const ITEMS_PER_SLIDE = 3;

type AccessoriesContentProps = {
  accessories: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['features']['accessories'];
};

export const AccessoriesContent: React.FC<AccessoriesContentProps> = ({
  accessories,
}) => {
  const isDesktop = useBreakpointValue({ md: true });
  const isAlreadyTracked = React.useRef(false);
  const mobileSliderRef = React.useRef<HTMLDivElement>();

  const catalogIds = accessories.products.map(
    (product) => `${product.catalogId}`
  );
  const { getTimeFromInit } = useTrackingContext();

  const { data, loading } = useProductsByIdsBatched(catalogIds, {
    idType: ProductIdType.Catalog,
  });

  const placeholderCount = Math.min(
    accessories.products.length,
    ITEMS_PER_SLIDE
  );
  const centerPlaceholder = placeholderCount < ITEMS_PER_SLIDE;

  const trackAddToCartFailedAsync = useTrackToCartFailedAsync(
    catalogIds,
    GTM_LIST_LABEL,
    true
  );
  const trackAddToCardAsync = useTrackToCartAsync(
    catalogIds,
    GTM_LIST_LABEL,
    true
  );

  const getProductItemProps = useGetProductItemProps({
    noPriceIfUnavailable: false,
    useMasterUrl: true,
    inSectionOfType: 'slider',
    wishlistActionLocation: 'list',
    showBuyButton: true,
    showAddToCartModal: false,
    onProductAddFailed: trackAddToCartFailedAsync,
    onProductAdded: trackAddToCardAsync,
    GTMEventLabel: GTM_LIST_LABEL,
  });

  React.useEffect(() => {
    if (!data || isAlreadyTracked.current) {
      return;
    }

    trackAccessories(
      data.vpProductByIds,
      catalogIds,
      isDesktop ? 'click' : 'scroll'
    );
    isAlreadyTracked.current = true;
  }, [data, isDesktop, isAlreadyTracked, catalogIds]);

  const products =
    data?.vpProductByIds
      .map((product, index) => {
        const isOutOfStock =
          product.stockAvailability.code.toLocaleLowerCase() ===
          StockAvailability.OutOfStock.toLocaleLowerCase();

        const hasOtherVariants = product.attributes?.HasOtherVariants;

        const unavailable = isOutOfStock && <Unavailable />;

        const otherVariants = hasOtherVariants && (
          <ShowOtherVariantsLink product={product} />
        );

        const appendComponent = unavailable || otherVariants;

        return getProductItemProps({
          product,
          position: index + 1,
          appendComponent,
          shouldHideItemBuyButton: hasOtherVariants,
        });
      })
      .filter(Boolean) ?? [];

  const onNewItemShow = React.useCallback(() => {
    if (isDesktop) {
      return;
    }

    if (mobileSliderRef?.current?.scrollLeft === 0) {
      return;
    }

    trackElementClick({
      timing: getTimeFromInit(),
      interaction: 'swipe',
      name: 'accessories_slide',
      type: 'accessories',
      action: 'accessories_slide',
    });
  }, [getTimeFromInit, isDesktop]);

  const onSlideChange = () => {
    trackElementClick({
      timing: getTimeFromInit(),
      interaction: 'click',
      name: 'accessories_slide',
      type: 'accessories',
      action: 'accessories_slide',
    });
  };

  return (
    <div>
      {!data || loading ? (
        <StyledLoadingPlaceholder
          placeholderCount={placeholderCount}
          rows={5}
          center={centerPlaceholder}
        />
      ) : (
        <SliderWrapper centerSlider={products.length < ITEMS_PER_SLIDE}>
          <ProductSlider
            itemsPerSlide={ITEMS_PER_SLIDE}
            mobileScrollContainerRef={mobileSliderRef}
            products={products ?? []}
            inViewThreshold={1}
            onSlideChange={onSlideChange}
            onItemShow={onNewItemShow}
            fallback={
              <StyledLoadingPlaceholder
                placeholderCount={placeholderCount}
                rows={4}
                center={centerPlaceholder}
              />
            }
          />
        </SliderWrapper>
      )}
    </div>
  );
};
